import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { StatusRole } from '@/lightTheme';

const latencyTone = (ms: number): StatusRole => {
  if (ms <= 100) return 'ok';
  if (ms <= 300) return 'attention';
  return 'problem';
};

interface LatencyBarProps {
  latencyMs: number | null;
  /** The latency that fills the bar; slower answers are drawn full. */
  maxMs?: number;
}

/** A measured latency: the figure in words and a bar on a fixed 0–1000 ms scale, toned by the
 *  thresholds (up to 100 ms good, up to 300 ms slow, beyond that a problem). */
export const LatencyBar: React.FC<LatencyBarProps> = ({ latencyMs, maxMs = 1000 }) => {
  const theme = useTheme();

  if (latencyMs === null) {
    return <Typography variant="body2" color="text.secondary" component="span">—</Typography>;
  }

  const width = Math.min((latencyMs / maxMs) * 100, 100);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box aria-hidden sx={{ flex: 1, height: 6, borderRadius: '3px', backgroundColor: theme.palette.grey[100], overflow: 'hidden' }}>
        <Box sx={{ width: `${width}%`, height: '100%', backgroundColor: theme.custom.status[latencyTone(latencyMs)].dot }} />
      </Box>
      <Typography variant="body2" component="span" sx={{ minWidth: 56, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {latencyMs} ms
      </Typography>
    </Box>
  );
};
